import './index.scss';

import { Component } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { BraxForm, BraxHeader, BraxSelectOption, BraxLoading, BraxCriticalError, BraxSelectField, BraxButton, BraxToasts, BraxToastsType, BraxModals, BraxModalsType, BraxModal } from 'design-system';
import { FormikErrors, FormikProps } from 'formik';
import { Asserts } from 'yup';

import { PaymentInterval } from '../../../variables/payment-interval';
import { PaymentMethod } from '../../../variables/payment-method';
import { PaymentInstallments } from '../../../variables/payment-installments';
import t from '../../../utils/i18n';
import handleError from '../../../utils/errors';
import { withContexts } from '../../../utils/with-context';
import payment, { PaymentToken } from '../../../utils/payment';
import checkoutSchema, { CheckoutValue } from '../../../schemas/plans/checkout';
import User from '../../../view-model/users/User';
import Plan from '../../../view-model/plans/Plan';
import usersService from '../../../services/users';
import plansService from '../../../services/plans';
import paymentsService from '../../../services/payments';
import analyticsService from '../../../services/analytics';
import UserContext from '../../../contexts/User';
import UpgradeUserPlan from '../UpgradeUserPlan';
import PlansFooter from '../../../components/PlansFooter';
import CheckoutStepOneFields from './CheckoutStepOneFields';
import CheckoutStepTwoFields from './CheckoutStepTwoFields';
import CheckoutStepThreeFields from './CheckoutStepThreeFields';

export type CheckoutProps = RouteComponentProps & {
    modals: BraxModalsType,
    toasts: BraxToastsType,
    user: User,
    plan?: number,
    coupon?: string,
    interval?: PaymentInterval,
    method?: PaymentMethod,
    installments?: PaymentInstallments
    open?: boolean,
    onClose?: () => void
};

type CheckoutState = {
    initialValues: Partial<Asserts<typeof checkoutSchema>>,
    plans: BraxSelectOption<Plan>[],
    stepTwoDisabled: boolean,
    stepThreeDisabled: boolean,
    loading: boolean,
    adBlocked: boolean,
    subscribing: boolean
};

export default withRouter(withContexts({
    modals: BraxModals,
    toasts: BraxToasts,
    user: UserContext
}, class Checkout extends Component<CheckoutProps, CheckoutState> {

    state: CheckoutState = {
        initialValues: {},
        plans: [],
        stepTwoDisabled: true,
        stepThreeDisabled: true,
        loading: true,
        adBlocked: false,
        subscribing: false
    };

    schema = checkoutSchema;

    validated = false;

    async componentDidMount() {
        if ((!this.props.user.plan.isFree && this.props.user.plan.id >= 1000) || this.props.user.subscription?.isActive) {
            this.props.onClose?.();

            this.props.modals.custom(<UpgradeUserPlan plan={this.props.plan} interval={this.props.interval} coupon={this.props.coupon} />);

            return;
        }

        try {
            var [plans] = await Promise.all([
                plansService.getPlans(),
                payment.load()
            ]);

            this.setState({
                plans: plans.result
                    .filter(plan => !plan.isFree)
                    .map(plan => ({
                        value: plan,
                        title: plan.title
                    }))
            });
        } catch (error) {
            this.props.onClose?.();

            this.setState({ loading: false });

            handleError(error);

            this.props.modals.custom(
                <BraxCriticalError title={t('Aviso')} description={t('Não foi possível carregar a tela. Por favor, tente novamente ou envie um e-mail para: contato@imersiovr.com.')} />
            );

            return;
        }

        try {
            const [
                { result: address },
                { result: taxData },
                // couponCode
            ] = await Promise.all([
                usersService.getUserAddress(this.props.user.id),
                usersService.getUserTaxData(this.props.user.id),
                // this.props.coupon || paymentsService.getCoupons(this.props.user.id).then(response => response.result[0]?.code)
            ]);

            this.setState({
                initialValues: {
                    plan: (this.props.plan !== undefined && plans.result.find(plan => !plan.isFree && plan.id === this.props.plan)) || plans.result.find(plan => !plan.isFree)!,
                    // ...(couponCode ? { couponCode } : {}),
                    ...(this.props.coupon ? { couponCode: this.props.coupon } : {}),
                    ...(this.props.method ? { paymentMethod: this.props.method } : {}),
                    ...(this.props.interval ? { interval: this.props.interval } : {}),
                    ...(this.props.installments ? { installments: this.props.installments } : {}),
                    payer: {
                        firstName: this.props.user.firstName,
                        lastName: this.props.user.lastName,
                        address: { country: 'Brazil', ...(address || {}) },
                        ...(taxData ? {
                            typePerson: taxData.typePerson,
                            cpf: taxData.typePerson === 'natural' ? taxData.document : '',
                            cnpj:taxData.typePerson === 'legal' ? taxData.document : '',
                            companyName: taxData.companyName,
                            // foreignId: taxData.foreignId,
                            foreignName: taxData.foreignName
                        } : {})
                    }
                } as any,
                loading: false
            });
        } catch (error) {
            this.props.toasts.create({ type: 'destructive', ...handleError(error, 'Falha ao carregar seus dados') });

            this.setState({ loading: false });
        }

        analyticsService.send({ user: this.props.user.id, event: 'opened_checkout_screen' });

        if (await payment.Iugu.utils.isBlockedByAdBlock())
            this.setState({ adBlocked: true });
    }

    checkDisabledSteps(errors: FormikErrors<CheckoutValue>) {
        if (this.validated && (this.state.stepTwoDisabled || this.state.stepThreeDisabled))
            setTimeout(() => this.setState(prevState => {
                let stepTwoDisabled = prevState.stepTwoDisabled;
                let stepThreeDisabled = prevState.stepThreeDisabled;

                if (stepTwoDisabled)
                    stepTwoDisabled = !!errors.payer && Object.keys(errors.payer).length > 0;

                if (stepThreeDisabled)
                    stepThreeDisabled = !!errors.paymentMethod || !!errors.interval || !!errors.installments ||
                        !!errors.creditCardName || !!errors.creditCardNumber || !!errors.creditCardExpiration || !!errors.creditCardCVV;

                return stepTwoDisabled !== prevState.stepTwoDisabled || stepThreeDisabled !== prevState.stepThreeDisabled
                    ? { stepTwoDisabled, stepThreeDisabled }
                    : null;
            }), 0);
    }

    async subscribe({ plan, payer: _payer, creditCardName, creditCardNumber, creditCardCVV, creditCardExpiration, ...values }: CheckoutValue) {
        this.setState({ subscribing: true });

        const { cpf, cnpj, firstName, lastName, companyName, foreignId, foreignName, address: _address, ...payer } = _payer;

        const { number, state, ...address } = _address;

        try {
            let paymentToken: PaymentToken | null = null;

            if (values.paymentMethod === 'credit_card') {
                const [month, year] = payment.Iugu.utils.getMonthYearByFullExpiration(creditCardExpiration) as [string, string];

                const [firstName, lastName] = payment.Iugu.utils.getFirstLastNameByFullName(creditCardName) as [string, string];

                paymentToken = await payment.Iugu.createPaymentToken(
                    payment.Iugu.CreditCard(creditCardNumber, month, year, firstName, lastName, creditCardCVV)
                );
            }

            await paymentsService.subscribe({
                idPlan: plan.id,
                user: this.props.user.id,
                customer: {
                    name: address.country !== 'Brazil'
                        ? foreignName
                    : payer.typePerson === 'legal'
                        ? companyName
                        : `${firstName} ${lastName}`
                },
                payer: {
                    document: address.country !== 'Brazil'
                        ? undefined // foreignId
                    : payer.typePerson === 'legal'
                        ? cnpj
                        : cpf,
                    address: {
                        numberAddress: number,
                        stateName: state,
                        ...address
                    },
                    ...payer
                },
                cardToken: paymentToken?.id!,
                ...values
            });

            const close = this.props.modals.create({
                className: 'successfully-signed-plan',
                close: false,
                backdrop: false,
                children: (<>
                    <h2>{ t('Assinatura concluída!') }</h2>

                    <p>{ t('Assinatura criada com sucesso! Após a confirmação do pagamento seu plano será liberado.') }</p>

                    <BraxButton variant="primary" onClick={() => close()}>{ t('Ir para Pagamentos') }</BraxButton>
                </>),
                onClose: () => this.props.history.push('/user#payment-history')
            });

            this.props.onClose?.();
        } catch (error: any) {
            if (error?.adblock)
                this.props.toasts.create({
                    type: 'destructive',
                    title: t('Falha ao assinar!'),
                    description: t('Algum AdBlock pode estar impedindo a assinatura. Remova e tente novamente.')
                });
            else
                this.props.toasts.create({ type: 'destructive', ...handleError(error, 'Falha ao gerar assinatura') });
        }

        this.setState({ subscribing: false });
    }

    render() {
        if (this.state.loading)
            return (<BraxLoading />);

        return (
            <BraxModal
                open={this.props.open}
                className="checkout"
                size="full"
                onClose={this.props.onClose}
            >
                { this.state.subscribing ? (
                    <BraxLoading />
                ) : null }

                <BraxForm
                    schema={this.schema}
                    values={this.state.initialValues}
                    onSubmit={values => this.subscribe(values as CheckoutValue)}
                    validateOnMount
                >
                    { formik => {
                        const values = formik.values as CheckoutValue;

                        if (values.paymentMethod !== 'credit_card' && (
                            values.interval === 'monthly' || values.payer.address.country !== 'Brazil'
                        ))
                            formik.setFieldValue('paymentMethod', 'credit_card');

                        if (values.installments !== 1 && (
                            values.interval === 'monthly' || values.paymentMethod === 'bank_slip'
                        ))
                            formik.setFieldValue('installments', 1);

                        if (!this.validated && Object.keys(formik.errors).length > 0)
                            this.validated = true;

                        if (!formik.isValidating)
                            this.checkDisabledSteps(formik.errors);

                        return (<>
                            <BraxHeader>
                                <div>
                                    {/* <BraxLogo type="logotype" variant="primary" /> */}

                                    <BraxSelectField
                                        name="plan"
                                        label={t('Plano selecionado')}
                                        options={this.state.plans}
                                        variant="arrow-on-label"
                                    />
                                </div>

                                {/* <p className="subtitle">{ t('Planos, Funcionalidades e Preços') }</p> */}
                            </BraxHeader>

                            { this.state.adBlocked ? (
                                <div className="adblock-warning">{ t('Pode ser que há algum AdBlock instalado no seu navegador. Remova para conseguir realizar a assinatura.') }</div>
                            ) : null }

                            <div>
                                <CheckoutStepOneFields
                                    isForeign={values.payer.address.country !== 'Brazil'}
                                    typePerson={values.payer.typePerson}
                                    formik={formik as FormikProps<CheckoutValue>}
                                />

                                <CheckoutStepTwoFields
                                    isForeign={values.payer.address.country !== 'Brazil'}
                                    method={values.paymentMethod}
                                    installments={values.installments}
                                    interval={values.interval}
                                    plan={values.plan}
                                    cardNumber={values.creditCardNumber}
                                    disabled={this.state.stepTwoDisabled}
                                />

                                <CheckoutStepThreeFields
                                    user={this.props.user}
                                    subscribing={this.state.subscribing}
                                    method={values.paymentMethod}
                                    interval={values.interval}
                                    installments={values.installments}
                                    plan={values.plan}
                                    cardNumber={values.creditCardNumber}
                                    coupon={values.couponCode}
                                    disabled={this.state.stepThreeDisabled}
                                    // onSelectCoupon={coupon => formik.setFieldValue('couponCode', coupon)}
                                />
                            </div>
                        </>);
                    } }
                </BraxForm>

                <PlansFooter />
            </BraxModal>
        );
    }

}));
